import { usageApi } from "@/lib/api";

/**
 * Web visit signal (SPEC-020). One decision function, `touchPresence`, is used
 * by every trigger. A visit starts when the user is present (tab visible) and
 * the last recorded presence is missing/invalid or older than the window.
 */

export const VISIT_WINDOW_MS = 30 * 60 * 1000;
export const VISIT_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;
export const VISIT_TICK_MS = 60 * 1000;
export const VISIT_RETRY_MIN_MS = 5000;
export const VISIT_RETRY_JITTER_MS = 5000;

const KEY_PREFIX = "harvn_visit_v1:";

const storageKey = (userId: string): string => `${KEY_PREFIX}${userId}`;

/** True when `now` starts a new visit given the previous presence timestamp. */
export function isNewVisit(lastSeen: number | null, now: number): boolean {
  if (lastSeen === null || !Number.isFinite(lastSeen)) return true;
  if (lastSeen > now + VISIT_FUTURE_TOLERANCE_MS) return true;
  return now - lastSeen > VISIT_WINDOW_MS;
}

function readLastSeen(userId: string): number | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw === null || !/^\d{1,16}$/.test(raw)) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeLastSeen(userId: string, now: number): void {
  try {
    localStorage.setItem(storageKey(userId), String(Math.trunc(now)));
  } catch {
    // storage unavailable: treated as missing on the next read
  }
}

/** Records presence only; never starts a visit (hidden / pagehide). */
export function writePresence(userId: string, now: number): void {
  writeLastSeen(userId, now);
}

function uuidv4(): string {
  const c: Crypto | undefined =
    typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

const pendingTimers = new Set<ReturnType<typeof setTimeout>>();
const inFlight = new Set<AbortController>();

function isRetryable(error: unknown): boolean {
  const e = error as
    | { code?: string; response?: { status?: number } }
    | null
    | undefined;
  if (e?.code === "ERR_CANCELED") return false;
  const status = e?.response?.status;
  if (status === undefined) return true; // network error / timeout
  return status >= 500 || status === 429;
}

async function attempt(visitId: string, isRetry: boolean): Promise<void> {
  let controller: AbortController | undefined;
  try {
    controller =
      typeof AbortController !== "undefined"
        ? new AbortController()
        : undefined;
    if (controller) inFlight.add(controller);
    await usageApi.recordVisit(visitId, controller?.signal);
  } catch (error) {
    if (isRetry || !isRetryable(error)) return;
    try {
      const delay =
        VISIT_RETRY_MIN_MS + Math.random() * VISIT_RETRY_JITTER_MS;
      const timer = setTimeout(() => {
        pendingTimers.delete(timer);
        void attempt(visitId, true);
      }, delay);
      pendingTimers.add(timer);
    } catch {
      // never throw
    }
  } finally {
    if (controller) inFlight.delete(controller);
  }
}

/** Fire-and-forget delivery with one bounded retry (same visit_id). */
function sendVisit(visitId: string): void {
  try {
    void attempt(visitId, false);
  } catch {
    // never throw
  }
}

/** Cancels the pending retry timers and aborts in-flight requests (logout). */
export function cancelPendingRetry(): void {
  pendingTimers.forEach((timer) => clearTimeout(timer));
  pendingTimers.clear();
  inFlight.forEach((controller) => {
    try {
      controller.abort();
    } catch {
      // ignore
    }
  });
  inFlight.clear();
}

/**
 * Synchronous check-write-send. Hidden tabs are ignored entirely (no visit,
 * no write). `lastSeen` is written BEFORE sending so a second trigger in the
 * same tick only refreshes it.
 */
export function touchPresence(userId: string, now: number = Date.now()): void {
  try {
    if (!userId || document.visibilityState !== "visible") return;
    const start = isNewVisit(readLastSeen(userId), now);
    writeLastSeen(userId, now);
    if (start) sendVisit(uuidv4());
  } catch {
    // never throw into callers
  }
}
